import React from "react";
import {StyleSheet, ScrollView, TouchableOpacity, Text as RNText, View} from "react-native";
import {useScrollMore, ScrollMoreHint} from "./component/ScrollMore";
import {BackButton, CIRCLE_BTN} from "./component/BackButton";
import {Box, HStack, Text, VStack, Spinner, Icon} from "native-base";
import {Ionicons} from '@expo/vector-icons';
import DashedLine from "react-native-dashed-line";
import {useNavigation, useRoute} from "@react-navigation/native";
import {useQuery} from "@tanstack/react-query";
import * as remote from "./lib/remote";
import {OrderSessionRes, OrderLine} from "./lib/order_model";
import apps from "./lib/apps";
import {PALETTE} from "./lib/palette";
import {money} from "./lib/utils";
import ThumbImage from "./component/ThumbImage";
import {buildCustomerCopy, hasReceipt} from "./lib/receipt";
import {usePauseIdleGuard} from "./state/idleState";

/** 카드 결제 주문이면 EFTPOS "CUSTOMER COPY" 영수증을 렌더. 카드가 아니면(빈 영수증) 아무것도 안 그림.
 *  주문마다 자체 useQuery로 findReceiptByOrderId를 호출한다(pay-at-counter 주문은 빈 응답 → null). */
function OrderCustomerCopy({orderId}: {orderId: number}) {
    const {data: receipt} = useQuery(
        ["receipt", orderId],
        () => remote.findReceiptByOrderId(orderId),
        {enabled: orderId > 0, retry: 0},
    );
    if (!hasReceipt(receipt)) return null;
    return (
        <Box style={styles.receiptWrap}>
            <Text style={styles.receiptText}>{buildCustomerCopy(receipt)}</Text>
        </Box>
    );
}

/**
 * 읽기전용 테이블 세션 주문 다시보기.
 * route params: { shopId, orderId }. 서버가 orderId로 txId를 해석 → 같은 세션 주문 목록.
 * pay-at-counter는 여러 주문, 카드는 1건. 재주문/취소 경로 없음. 시각·일자는 매장 State 시간대(서버 계산).
 */
function OrderReview() {
    usePauseIdleGuard();   // 읽기전용 다시보기 화면 — 무활동 초기화 정지
    const accent = apps.getAccent();
    const tint = apps.accentTint();
    const navigation = useNavigation<any>();
    const route = useRoute<any>();
    const {shopId, orderId} = route.params ?? {};

    const {data, isLoading, error} = useQuery<OrderSessionRes>(
        ["orderSession", shopId, orderId],
        () => remote.getOrderSession(shopId, orderId),
        {enabled: !!shopId && orderId > 0, retry: 1},
    );
    const {showHint, scrollToEnd, scrollProps} = useScrollMore();   // 스크롤 "더 있음" 힌트

    // 세션 주문을 날짜(placedDate)별로 묶는다. 서버가 placed 오름차순으로 주므로 연속 그룹핑이면 충분.
    // (자정 넘겨 영업하는 매장은 한 세션이 두 날짜에 걸칠 수 있음.)
    const orders = data?.orders ?? [];
    const groups: {date: string; orders: OrderLine[]}[] = [];
    for (const o of orders) {
        const last = groups[groups.length - 1];
        if (last && last.date === (o.placedDate ?? "")) last.orders.push(o);
        else groups.push({date: o.placedDate ?? "", orders: [o]});
    }

    return (
        <VStack style={styles.screen}>
            {/* Header */}
            <HStack style={styles.header}>
                <BackButton
                    onPress={() => navigation.goBack()}
                    containerStyle={CIRCLE_BTN}
                    size={12}
                    thickness={2.5}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                    blink={false}
                />
                {/* RN Text로 렌더(native-base Text는 fontWeight 900이 얇게 나와 다른 화면과 굵기 불일치) */}
                <RNText style={styles.headerTitle}>Your Order</RNText>
                <Box style={styles.back}/>
            </HStack>

            {isLoading ? (
                <Box style={styles.center}><Spinner color={accent} size="lg"/></Box>
            ) : error || !data || data.orders.length === 0 ? (
                <Box style={styles.center}>
                    <Text style={styles.muted}>No orders to show.</Text>
                </Box>
            ) : (
                <View style={{flex: 1}}>
                <ScrollView {...scrollProps} style={{flex: 1}} contentContainerStyle={{padding: 16, paddingBottom: 32}}>
                    {/* Session summary */}
                    <HStack style={styles.summary}>
                        <VStack>
                            <Text style={styles.summaryLabel}>TABLE</Text>
                            <Text style={styles.summaryTable}>{data.tableNo || "-"}</Text>
                        </VStack>
                        <VStack style={{alignItems: "flex-end"}}>
                            <Text style={styles.summaryLabel}>ORDERS</Text>
                            <Text style={[styles.summaryCount, { color: accent }]}>
                                {data.orders.length} {data.orders.length > 1 ? "orders" : "order"}
                            </Text>
                        </VStack>
                    </HStack>

                    {groups.map((g) => (
                        <VStack key={g.date}>
                            <HStack style={styles.dateHeader}>
                                <Text style={styles.dateHeaderText}>{g.date}</Text>
                            </HStack>
                            {g.orders.map((o: OrderLine) => {
                        // 공휴일 서차지 유도: o.subtotal(카드=원 결제 전 금액=소계+서차지, 비카드=order.total)에서
                        // o.total(항목 소계, 순수)을 뺀 값. 비카드는 항상 0(subtotal===total). 서버 변경 없이 앱에서
                        // 분해(final-findings Important 2). 부동소수 뺄셈 금지 — 센트 정수로 계산.
                        const holidayCents = Math.round(o.subtotal * 100) - Math.round(o.total * 100);
                        const holidayAmt = holidayCents > 0 ? holidayCents / 100 : 0;
                        return (
                        <Box key={o.orderId} style={styles.card}>
                            <HStack style={styles.orderHead}>
                                <Box style={[styles.orderPill, {backgroundColor: tint}]}>
                                    <Text style={[styles.orderPillText, { color: accent }]}>
                                        {o.ordNo > 0 ? "Order #" + o.ordNo : "Order"}
                                    </Text>
                                </Box>
                                <Text style={styles.orderTime}>{o.placedTime}</Text>
                            </HStack>

                            {o.status === "CANCELED" && (
                                <Box style={styles.canceledBadge}>
                                    <Text style={styles.canceledText}>CANCELED</Text>
                                </Box>
                            )}

                            <VStack style={styles.items}>
                                {o.items.map((it, idx) => (
                                    <React.Fragment key={idx}>
                                        <HStack style={styles.itemRow}>
                                            {/* 썸네일: 서버가 menuId를 주면(배포 후) 표시. 미배포면 생략. */}
                                            {it.menuId != null ? (
                                                <ThumbImage shopid={shopId} menuid={it.menuId} imageVer={it.imageVer ?? 0} style={styles.itemThumb} />
                                            ) : null}
                                            {/* 수량은 다른 화면과 동일하게 오른쪽(이름칸 확보) */}
                                            <VStack style={{flex: 1}}>
                                                <Text style={styles.itemName}>{it.name}</Text>
                                                {it.options ? <Text style={styles.itemOptions}>{it.options}</Text> : null}
                                                {it.note ? <Text style={styles.itemNote}>📝 {it.note}</Text> : null}
                                            </VStack>
                                            <VStack style={styles.itemQtyRight}>
                                                <Text style={styles.itemAmt}>{money(it.amount)}</Text>
                                                <Box style={styles.itemQtyChip}><Text style={styles.itemQtyChipText}>×{it.qty}</Text></Box>
                                            </VStack>
                                        </HStack>
                                        {/* 아래 Order Total 구분선(PALETTE.DIVIDER)과 톤을 맞춘 가는 점선 */}
                                        {idx < o.items.length - 1 ? (
                                            <DashedLine dashLength={3} dashGap={3} dashThickness={1} dashColor={PALETTE.DIVIDER} style={{height: 1}} />
                                        ) : null}
                                    </React.Fragment>
                                ))}
                            </VStack>

                            <VStack style={styles.orderTotalRow}>
                                {(o.surcharge > 0 || holidayAmt > 0) ? (
                                    <>
                                        <HStack style={styles.subRow}>
                                            <Text style={styles.subLabel}>Subtotal</Text>
                                            <Text style={styles.subVal}>{money(o.total)}</Text>
                                        </HStack>
                                        {holidayAmt > 0 && (
                                            <HStack style={styles.subRow}>
                                                <Text style={styles.subLabel}>Holiday Surcharge</Text>
                                                <Text style={styles.subVal}>{money(holidayAmt)}</Text>
                                            </HStack>
                                        )}
                                        {o.surcharge > 0 && (
                                            <HStack style={styles.subRow}>
                                                <Text style={styles.subLabel}>Processing Fee</Text>
                                                <Text style={styles.subVal}>{money(o.surcharge)}</Text>
                                            </HStack>
                                        )}
                                        <HStack style={styles.subRow}>
                                            <Text style={styles.orderTotalLabel}>Total</Text>
                                            <Text style={styles.orderTotalVal}>{money(o.grandTotal)}</Text>
                                        </HStack>
                                    </>
                                ) : (
                                    <HStack style={styles.subRow}>
                                        <Text style={styles.orderTotalLabel}>Order Total</Text>
                                        <Text style={styles.orderTotalVal}>{money(o.total)}</Text>
                                    </HStack>
                                )}
                            </VStack>

                            <OrderCustomerCopy orderId={o.orderId} />
                        </Box>
                        );
                            })}
                        </VStack>
                    ))}

                    {/* Table Total — 1행 "QR Order", 2행 "Total" + 금액 */}
                    <VStack style={[styles.tableTotalRow, {backgroundColor: tint, borderColor: PALETTE.BORDER}]}>
                        <Text style={styles.tableTotalTitle}>QR Order</Text>
                        <HStack style={styles.tableTotalLine}>
                            <Text style={styles.tableTotalLabel}>Total</Text>
                            <Text style={[styles.tableTotalVal, { color: accent }]}>{money(data.tableTotal)}</Text>
                        </HStack>
                    </VStack>

                    <Text style={styles.footNote}>To order again, scan the table QR code.</Text>
                </ScrollView>
                <ScrollMoreHint visible={showHint} onPress={scrollToEnd}/>
                </View>
            )}
        </VStack>
    );
}

const styles = StyleSheet.create({
    screen: {flex: 1, backgroundColor: PALETTE.BG},
    header: {width: "100%", height: 56, alignItems: "center", justifyContent: "space-between", paddingHorizontal: 14, backgroundColor: "#fff", borderBottomWidth: 1, borderBottomColor: PALETTE.BORDER},
    back: {width: 26, height: 26},
    headerTitle: {fontSize: 18, fontFamily: "NotoSans-Black", fontWeight: "900", color: PALETTE.TEXT, letterSpacing: -0.4},
    center: {flex: 1, alignItems: "center", justifyContent: "center"},
    muted: {color: PALETTE.MUTED, fontSize: 15, fontWeight: "700"},

    summary: {justifyContent: "space-between", alignItems: "flex-end", paddingHorizontal: 4, marginBottom: 14},
    summaryLabel: {fontSize: 11, color: PALETTE.MUTED, fontWeight: "800", letterSpacing: 0.7},
    summaryTable: {fontSize: 26, color: PALETTE.TEXT, fontWeight: "900", letterSpacing: -0.5, marginTop: 2},
    summaryCount: {fontSize: 15, color: PALETTE.ACCENT, fontWeight: "900", marginTop: 2},

    dateHeader: {marginTop: 4, marginBottom: 10, paddingBottom: 6, borderBottomWidth: 1, borderBottomColor: PALETTE.BORDER},
    dateHeaderText: {fontSize: 14, fontWeight: "900", color: PALETTE.MUTED, letterSpacing: -0.3},

    card: {backgroundColor: "#fff", borderRadius: PALETTE.RADIUS_LG, padding: 16, marginBottom: 12, borderWidth: 1, borderColor: PALETTE.BORDER},
    orderHead: {justifyContent: "space-between", alignItems: "center"},
    orderPill: {backgroundColor: "transparent", borderRadius: PALETTE.RADIUS_PILL, paddingVertical: 4, paddingHorizontal: 12},
    orderPillText: {fontSize: 14, fontWeight: "900", color: PALETTE.ACCENT, letterSpacing: -0.3},
    orderTime: {fontSize: 13, color: PALETTE.MUTED, fontWeight: "800"},

    canceledBadge: {alignSelf: "flex-start", marginTop: 8, backgroundColor: "rgba(214,69,69,0.12)", borderRadius: 6, paddingVertical: 2, paddingHorizontal: 8},
    canceledText: {color: "#c0392b", fontSize: 11, fontWeight: "900", letterSpacing: 0.5},

    items: {marginTop: 10},
    itemRow: {alignItems: "center", paddingVertical: 8},
    itemThumb: {width: 36, height: 36, borderRadius: PALETTE.RADIUS_SM, marginRight: 8, backgroundColor: PALETTE.BORDER},
    itemName: {flex: 1, color: PALETTE.TEXT, fontSize: 14, fontWeight: "600", marginRight: 8},
    itemOptions: {color: PALETTE.MUTED, fontSize: 12, marginTop: 2, marginRight: 8},
    itemNote: {color: PALETTE.MUTED, fontSize: 11, fontStyle: "italic", marginTop: 2, marginRight: 8},
    itemQtyRight: {alignItems: "flex-end"},
    itemAmt: {color: PALETTE.TEXT, fontWeight: "800"},
    itemQtyChip: {marginTop: 4, borderRadius: PALETTE.RADIUS_PILL, paddingHorizontal: 8, paddingVertical: 1, backgroundColor: PALETTE.QTY_CHIP},
    itemQtyChipText: {fontSize: 11, fontWeight: "700", color: "#FFFFFF", letterSpacing: -0.2},

    orderTotalRow: {marginTop: 10, borderTopWidth: 1, borderTopColor: PALETTE.DIVIDER, paddingTop: 10},
    subRow: {justifyContent: "space-between", alignItems: "center", paddingVertical: 2},
    subLabel: {color: PALETTE.MUTED, fontWeight: "700", fontSize: 13},
    subVal: {color: PALETTE.MUTED, fontWeight: "700", fontSize: 13},
    orderTotalLabel: {color: PALETTE.MUTED, fontWeight: "800"},
    orderTotalVal: {color: PALETTE.TEXT, fontWeight: "900", fontSize: 15},

    receiptWrap: {alignSelf: "center", width: "100%", maxWidth: 360, backgroundColor: "#fff", borderWidth: 1, borderColor: PALETTE.BORDER, borderRadius: PALETTE.RADIUS_SM, paddingVertical: 16, paddingHorizontal: 14, marginTop: 14},
    receiptText: {fontFamily: "monospace", fontSize: 12, lineHeight: 18, color: PALETTE.TEXT, textAlign: "center"},

    tableTotalRow: {marginTop: 4, padding: 18, backgroundColor: "transparent", borderRadius: PALETTE.RADIUS_LG, borderWidth: 1, borderColor: PALETTE.BORDER},
    tableTotalTitle: {fontSize: 16, fontWeight: "900", color: PALETTE.TEXT, letterSpacing: -0.3, marginBottom: 6},
    tableTotalLine: {justifyContent: "space-between", alignItems: "center"},
    tableTotalLabel: {flexShrink: 1, marginRight: 10, fontSize: 16, fontWeight: "900", color: PALETTE.TEXT},
    tableTotalVal: {fontSize: 22, fontWeight: "900", color: PALETTE.ACCENT, letterSpacing: -0.5},

    footNote: {textAlign: "center", color: PALETTE.MUTED, fontSize: 13, fontWeight: "700", marginTop: 20},
});

export default OrderReview;
